import { ALL_GRAPH_RECOMMENDATION_OFFER_TYPES_NO_LPP_OR_MARRIOTT } from 'constants/recommendations'
import { without } from 'lib/array/arrayUtils'
import { getUnit } from 'lib/geo/distanceUnits'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { createSelector } from 'reselect'
import { getCurrentUserId, selectDomainUserId } from './accountSelectors'

export const getDistanceUnit = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (regionCode) => {
    return getUnit(regionCode)
  },
)
export const getOfferExpired = createSelector(
  (state: App.State, offerId: string) => state.offer.offers[offerId],
  hasOfferExpired,
)

export const getExpiredOfferRecommendationsState = createSelector(
  (state: App.State, offerId: string) => state.offer.offers[offerId] ?? state.offer.offerSummaries[offerId],
  (state: App.State, offerId: string) => state.recommendations.recommendations[offerId],
  (offer, recommendationsState) => {
    const offerExpired = hasOfferExpired(offer)
    const offerIds = recommendationsState?.offerIds || []

    const loadingState: App.ApiResponseState = (() => {
      if (!recommendationsState) {
        return 'loading'
      }

      return recommendationsState.state
    })()

    return {
      offerExpired,
      offerIds,
      loadingState,
      displayMinLimit: offerIds.length > 1,
    }
  },
)

export const getOfferBasedLereState = createSelector(
  (state: App.State, seedOffer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary) => seedOffer,
  (state: App.State, offer: App.AnyOffer) => state.recommendations.recommendations[offer?.id],
  (seedOffer, recommendationsForOfferId) => {
    const recommendationOfferIds = without(recommendationsForOfferId?.offerIds ?? [], (seedOffer as App.AnyOffer)?.id)
    const recommendationLoadingError = recommendationsForOfferId?.error
    const recommendationLoadingState = recommendationsForOfferId?.state

    return {
      offerIds: recommendationOfferIds,
      hasEnoughRecommendationsToDisplay: recommendationOfferIds.length > 1,
      loadingState: recommendationLoadingState,
      loadingError: recommendationLoadingError,
    }
  },
)

export function isOfferAvailable(offer: App.Offer | App.OfferSummary) {
  // may not work for other types than hotel
  // so if a property is undefined we consider it as positive
  const isSoldOut = !!offer?.isSoldOut

  const isOnlinePurchaseActive = (offer && offer.onlinePurchaseSchedule === undefined) || scheduleIsCurrent(offer?.onlinePurchaseSchedule)

  return !isSoldOut && isOnlinePurchaseActive
}

export const selectTwoTowerOfferListFiltersByUser = createSelector(
  (state: App.State) => getCurrentUserId(state),
  (state: App.State) => selectDomainUserId(state),
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (leUserId, domainUserId, isLuxPlus): App.OfferListFilters => ({
    lere: 'twoTowers',
    leUserId,
    domainUserId,
    isLuxPlus,
    offerTypes: ALL_GRAPH_RECOMMENDATION_OFFER_TYPES_NO_LPP_OR_MARRIOTT as Array<App.OfferListOfferType>,
    rankerEnabled: false,
    useClusters: false,
  }),
)

export const selectAlternativesOfferListFilters = createSelector(
  (state: App.State) => getCurrentUserId(state),
  (state: App.State) => selectDomainUserId(state),
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (leUserId, domainUserId, isLuxPlus): App.OfferListFilters => ({
    lere: 'alternatives',
    leUserId,
    domainUserId,
    isLuxPlus,
  }),
)
